import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "@/lib/brand";

function Shell({ title, legal = false, children }) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <h1 className="font-display text-4xl font-bold tracking-tight mb-6">{title}</h1>
      <div className="prose prose-sm max-w-none space-y-4 text-[15px] leading-relaxed">{children}</div>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-display text-xl font-bold mt-6 mb-1">{children}</h2>;
const ContactLink = ({ children = "our contact form" }) => (
  <Link to="/contact" className="text-brand underline underline-offset-2">{children}</Link>
);

export function HowItWorks() {
  const steps = [
    ["Write your Call", "Type any prediction about the future."],
    ["Lock It In", "We record the exact wording and the moment you locked it."],
    ["Get your receipt", "A permanent, shareable page proves what you said and when."],
    ["Prove it later", "When the outcome happens, resolve it with evidence and share your win."],
  ];
  return (
    <Shell title="How It Works">
      <p className="text-lg">{BRAND.name} is the easiest way to say “I called it” — and actually prove it.</p>
      <ol className="space-y-4 list-none pl-0">
        {steps.map(([t, d], i) => (
          <li key={i} className="card-brutal p-4 bg-white flex gap-3">
            <span className="font-display text-2xl font-bold text-brand">{i + 1}</span>
            <div><div className="font-bold">{t}</div><div className="text-muted-foreground">{d}</div></div>
          </li>
        ))}
      </ol>
      <Link to="/" className="inline-block h-11 px-5 border-2 border-ink bg-brand text-white font-bold btn-press shadow-brutal-sm leading-[42px]">Make your first Call</Link>
    </Shell>
  );
}

export function HowReceipts() {
  return (
    <Shell title="How Receipts Work">
      <ol className="list-decimal pl-5 space-y-2">
        <li>You write a Call.</li>
        <li>You press Lock It In.</li>
        <li>Our server records the exact Call and time.</li>
        <li>We generate a cryptographic fingerprint (SHA-256) of the original.</li>
        <li>The original Call can never be edited.</li>
        <li>Any later updates are stored separately as notes.</li>
        <li>When the outcome happens, evidence can resolve the Call.</li>
        <li>Anyone can return to the original receipt.</li>
      </ol>
      <div className="card-brutal p-4 bg-secondary">
        <p className="font-bold">The technical bit (optional)</p>
        <p className="text-muted-foreground">Each Call is hashed over a canonical representation of its immutable fields
        (prediction text, author reference, timestamp, deadline, confidence and a random nonce). The hash lets anyone verify
        the original content hasn't changed. Note: a database timestamp proves when <b>our platform</b> recorded the Call — it is
        not, by itself, an independent third-party timestamp. The architecture supports adding external trusted timestamping later.</p>
      </div>
    </Shell>
  );
}

export function ResolutionRules() {
  return (
    <Shell title="Resolution Rules">
      <p>A Call can be resolved as <b>Called It</b>, <b>Missed It</b>, <b>Partially Correct</b>, or <b>Ambiguous</b>.</p>
      <ul className="list-disc pl-5 space-y-2">
        <li>Only the author can request resolution.</li>
        <li>Authors can self-declare a miss or an ambiguous outcome.</li>
        <li>Success claims are <b>not</b> auto-approved — they open for community verification and admin review.</li>
        <li>Evidence links must be provided for public resolutions.</li>
        <li>The original prediction and timestamp are never changed by resolution.</li>
        <li>“Days early” is measured from the original locked time to the outcome date.</li>
      </ul>
    </Shell>
  );
}

export function Guidelines() {
  return (
    <Shell title="Community Guidelines" legal>
      <p className="text-lg"><b>The short version:</b> Make bold Calls, keep it fun, and remember real people read this.</p>
      <p>{BRAND.name} is a place to put predictions on the record and see who called it first. To keep it welcoming, follow these rules.</p>
      <H>Do</H>
      <ul className="list-disc pl-5 space-y-1">
        <li>Make Calls about anything you genuinely predict.</li>
        <li>Disagree openly — start a Counter-Call instead of attacking the person.</li>
        <li>Back up resolutions with real evidence.</li>
        <li>Report content that breaks these rules.</li>
      </ul>
      <H>Don't</H>
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Harass, bully, or abuse</b> anyone, or use hate speech targeting people based on who they are.</li>
        <li><b>Threaten violence</b> or celebrate harm.</li>
        <li><b>Predict, encourage, or gamify harm</b> to specific private individuals — including death, injury, self-harm, or targeted misfortune. We don't allow leaderboards or “wins” around hurting people.</li>
        <li><b>Impersonate</b> others. Guest names aren't verified, so don't pose as a real person or brand to mislead.</li>
        <li><b>Post private information</b> about someone without consent (doxxing).</li>
        <li><b>Post illegal content</b>, or use {BRAND.name} for fraud, scams, or manipulation.</li>
        <li><b>Spam</b> — mass-posting vague or duplicate Calls to game reputation, or manipulating reactions, comments, or resolutions.</li>
        <li><b>Post sexual content involving minors</b> or other clearly prohibited material. Ever.</li>
      </ul>
      <H>A note on predictions vs. the real world</H>
      <p>A Call is a prediction, not a fact. Recording a Call doesn't make it true, and a resolution is a good-faith judgment that can be disputed. {BRAND.name} is not a betting or gambling service.</p>
      <H>How we enforce</H>
      <p>Depending on severity, we may hide a Call from public view, remove comments, mark content invalid, restrict or suspend accounts, or preserve internal records for safety and audit purposes. Because Calls are immutable, moderation changes visibility and status — it doesn't secretly rewrite what was recorded.</p>
      <H>Reporting</H>
      <p>Use the Report option on any Call, comment, or profile, or reach our team through <ContactLink />. Sensitive content is prioritized.</p>
    </Shell>
  );
}

export function Terms() {
  return (
    <Shell title="Terms of Service" legal>
      <p className="text-sm text-muted-foreground"><b>Effective date: 17 August 2026</b></p>
      <p>Welcome to {BRAND.name} (“{BRAND.name},” “we,” “us”). These Terms govern your use of called-it.me and our services (the “Service”). By using the Service you agree to these Terms. If you don't agree, please don't use the Service.</p>
      <H>1. What {BRAND.name} is (and isn't)</H>
      <p>{BRAND.name} lets people record predictions (“Calls”), timestamp them, and share them. We record <b>what</b> you predicted and <b>when</b> you recorded it.</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>We do <b>not</b> guarantee that any prediction is true, will come true, or has come true.</li>
        <li>A {BRAND.name} timestamp proves when <b>our platform recorded</b> a Call. It is not, by itself, an independent third-party legal timestamp.</li>
        <li>{BRAND.name} is <b>not</b> a gambling, betting, wagering, lottery, investment, or real-money prediction-market service. There are no deposits, stakes, or cash prizes. The only “currency” is reputation.</li>
      </ul>
      <H>2. Eligibility</H>
      <p>You must be at least 16 years old and able to form a binding contract. If you use the Service on behalf of an organization, you represent that you're authorized to accept these Terms for it.</p>
      <H>3. Guests and accounts</H>
      <ul className="list-disc pl-5 space-y-1">
        <li>You can make Calls as a <b>guest</b> by providing a display name. Guest display names are <b>not verified identities</b> and do not represent that any real-world person made the Call.</li>
        <li>Registered accounts (via Google or email login) get a profile, a permanent track record, and social features. You're responsible for activity under your account.</li>
        <li>You may claim an eligible guest Call only if you hold the secure claim credential issued when that Call was created.</li>
      </ul>
      <H>4. Your content and the immutability rule</H>
      <ul className="list-disc pl-5 space-y-1">
        <li>You retain ownership of the content you submit. You grant us a worldwide, non-exclusive, royalty-free license to host, display, reproduce, and distribute it to operate and promote the Service (including social share cards and previews).</li>
        <li><b>Once a Call is locked, its prediction text, original timestamp, deadline, and confidence cannot be edited or deleted by you.</b> This immutability is the core of the product. Later context is added only as separate, clearly-marked notes.</li>
        <li>If content is removed for moderation, we may retain internal records as needed for safety, legal, and audit purposes rather than rewriting history.</li>
      </ul>
      <H>5. Acceptable use</H>
      <p>You agree not to: post illegal content; harass, threaten, or abuse others; impersonate anyone; post another person's private information; predict, encourage, or gamify harm to specific individuals; spam or manipulate feeds, reactions, or resolutions; or attempt to break, scrape, or overload the Service. See our <Link to="/guidelines" className="text-brand underline underline-offset-2">Community Guidelines</Link>.</p>
      <H>6. Resolution and disputes</H>
      <p>Outcomes of Calls can be requested for resolution with evidence and may be reviewed by the community and/or our moderators. Resolutions are our and the community's good-faith assessment and may be disputed, changed, or marked ambiguous. We make no warranty that any resolution is objectively correct.</p>
      <H>7. Moderation</H>
      <p>We may hide, restrict, or remove content or accounts that violate these Terms or the Community Guidelines, at our discretion.</p>
      <H>8. Ads and third-party services</H>
      <p>The Service may display advertising (e.g., Google AdSense) and rely on third parties for sign-in (Google), email (Resend), and hosting. Their use of data is governed by their own terms and our <Link to="/privacy" className="text-brand underline underline-offset-2">Privacy Policy</Link>.</p>
      <H>9. Disclaimers</H>
      <p>The Service is provided “as is” and “as available,” without warranties of any kind, to the fullest extent permitted by law. We don't warrant that predictions, resolutions, timestamps, or content are accurate, reliable, or fit for any purpose.</p>
      <H>10. Limitation of liability</H>
      <p>To the maximum extent permitted by law, {BRAND.name} and its operators will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss arising from your reliance on any Call, resolution, or timestamp.</p>
      <H>11. Indemnity</H>
      <p>You agree to indemnify and hold {BRAND.name} harmless from claims arising out of your content or your violation of these Terms.</p>
      <H>12. Termination</H>
      <p>You may stop using the Service at any time. We may suspend or terminate access for violations. Some records (e.g., public Calls, audit logs) may persist as described in these Terms and the Privacy Policy.</p>
      <H>13. Changes</H>
      <p>We may update these Terms. We'll post the new effective date and, where appropriate, notify you. Continued use means you accept the changes.</p>
      <H>14. Governing law</H>
      <p>These Terms are governed by the laws of the State of Victoria, Australia, without regard to conflict-of-laws rules. Disputes will be handled in the courts of Victoria, Australia.</p>
      <H>15. Contact</H>
      <p>Questions? Reach us through <ContactLink />.</p>
    </Shell>
  );
}

export function Privacy() {
  return (
    <Shell title="Privacy Policy" legal>
      <p className="text-sm text-muted-foreground"><b>Effective date: 17 August 2026</b></p>
      <p>This Policy explains what we collect and how we use it when you use called-it.me.</p>
      <H>1. Who we are</H>
      <p>{BRAND.name} is operated by Andrew Falzon as an individual (sole operator), based in Melbourne, Australia. The data controller is Andrew Falzon, reachable through <ContactLink />.</p>
      <H>2. What we collect</H>
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Account data:</b> email address and, for Google sign-in, your name and avatar. You choose a username, display name, and optional bio.</li>
        <li><b>Content you create:</b> Calls (prediction text, timestamp, deadline, confidence, category, visibility), reactions, comments, resolutions and evidence links, follows, and reports.</li>
        <li><b>Guest data:</b> the display name you enter for a guest Call, and a secure claim token stored to let you claim it later.</li>
        <li><b>Technical data:</b> log data, device/browser info, and cookies/session identifiers needed to keep you signed in.</li>
        <li><b>Product analytics:</b> in-app events (e.g., call locked, receipt viewed, shared) used to understand and improve the Service.</li>
      </ul>
      <H>3. How we use it</H>
      <p>To operate the Service; create and display Calls and receipts; authenticate you; enable social features; generate share images and previews; maintain safety, moderation, and audit records; and improve the product.</p>
      <H>4. Public information</H>
      <p>Public Calls, receipts, and profiles are <b>visible to anyone</b> and may be indexed by search engines and shown in social previews. <b>Unlisted</b> Calls are reachable only by direct link and excluded from feeds/search. Guest display names you enter are shown publicly and are not verified.</p>
      <H>5. Cookies and sessions</H>
      <p>We use a secure, http-only session cookie to keep you logged in. We don't use it to track you across other sites. Advertising partners (below) may set their own cookies.</p>
      <H>6. Third parties we share data with</H>
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Google</b> — sign-in (OAuth).</li>
        <li><b>Resend</b> — sending login codes and transactional email.</li>
        <li><b>Google AdSense</b> — advertising; AdSense may use cookies/identifiers to serve and measure ads, subject to Google's policies.</li>
        <li><b>Hosting/infrastructure providers</b> — to run the Service.</li>
      </ul>
      <p>We do <b>not</b> sell your personal information.</p>
      <H>7. Data retention and the immutability rule</H>
      <p>Because {BRAND.name} is a permanent record of predictions, locked Calls and related audit events are retained as part of the public record. If you delete your account, we will remove or de-identify your personal profile data, but previously public Calls and audit logs may be retained or shown in an anonymized/attributed-to-deleted-user form, as described in the Terms.</p>
      <H>8. Your choices and rights</H>
      <p>Depending on your location, you may have rights to access, correct, export, or delete your personal data, and to object to certain processing. Reach us through <ContactLink />. Note that immutability limits edits to the content of locked Calls.</p>
      <H>9. Children</H>
      <p>The Service isn't directed to children under 16. We don't knowingly collect their data.</p>
      <H>10. International transfers</H>
      <p>Your data may be processed in countries other than yours, with appropriate safeguards where required.</p>
      <H>11. Security</H>
      <p>We use reasonable technical and organizational measures (e.g., encrypted transport, hashed Call fingerprints, secure sessions). No system is perfectly secure.</p>
      <H>12. Changes</H>
      <p>We'll post updates with a new effective date.</p>
      <H>13. Contact</H>
      <p>Reach us through <ContactLink />.</p>
    </Shell>
  );
}
